/**
 * Standard Modem Capability Definitions
 *
 * This registry defines the standard capabilities that modems can have.
 * New capabilities can be added here without changing code.
 */
export const STANDARD_CAPABILITIES = [
    // Network Technology Support
    {
        id: 'supports5G',
        displayName: '5G Support',
        description: 'Modem supports 5G NR (New Radio) technology',
        type: 'boolean',
        category: 'network',
        defaultValue: false,
        order: 1,
        required: false
    },
    {
        id: 'supports4G',
        displayName: '4G LTE Support',
        description: 'Modem supports 4G LTE technology',
        type: 'boolean',
        category: 'network',
        defaultValue: false,
        order: 2,
        required: true
    },
    {
        id: 'supports3G',
        displayName: '3G Support',
        description: 'Modem supports 3G (UMTS/HSPA) technology',
        type: 'boolean',
        category: 'network',
        defaultValue: false,
        order: 3,
        required: false
    },
    {
        id: 'supports2G',
        displayName: '2G Support',
        description: 'Modem supports 2G (GSM/GPRS/EDGE) technology',
        type: 'boolean',
        category: 'network',
        defaultValue: false,
        order: 4,
        required: false
    },
    // Advanced Features
    {
        id: 'supportsVoLTE',
        displayName: 'VoLTE',
        description: 'Voice over LTE support',
        type: 'boolean',
        category: 'features',
        defaultValue: false,
        order: 10,
        required: false
    },
    {
        id: 'supportseSIM',
        displayName: 'eSIM',
        description: 'Embedded SIM support',
        type: 'boolean',
        category: 'features',
        defaultValue: false,
        order: 11,
        required: false
    },
    {
        id: 'supportsVoNR',
        displayName: 'VoNR',
        description: 'Voice over 5G NR support',
        type: 'boolean',
        category: 'features',
        defaultValue: false,
        order: 12,
        required: false
    },
    {
        id: 'supportsGPS',
        displayName: 'GPS/GNSS',
        description: 'Built-in GPS/GNSS support. Deprecated — prefer `gnssConstellations` for which systems are supported.',
        type: 'boolean',
        category: 'features',
        defaultValue: false,
        order: 13,
        required: false,
        deprecated: true
    },
    {
        id: 'gnssConstellations',
        displayName: 'GNSS Constellations',
        description: 'Satellite systems supported by the built-in GNSS receiver',
        type: 'array',
        options: ['GPS', 'GLONASS', 'BDS', 'Galileo', 'QZSS', 'NavIC', 'SBAS'],
        category: 'features',
        defaultValue: [],
        order: 14,
        required: false
    },
    {
        id: 'hasGnss',
        displayName: 'GNSS Receiver',
        description: 'Modem has a usable GNSS receiver (any constellation). Allocator-friendly boolean derived from gnssConstellations being non-empty; used by flows that need a GPS fix.',
        type: 'boolean',
        category: 'features',
        defaultValue: false,
        order: 15,
        required: false
    },
    // IoT Capabilities
    {
        id: 'supportsLTEM',
        displayName: 'LTE-M (Cat-M1)',
        description: 'LTE Cat-M1 for IoT devices',
        type: 'boolean',
        category: 'iot',
        defaultValue: false,
        order: 20,
        required: false
    },
    {
        id: 'supportsNBIoT',
        displayName: 'NB-IoT',
        description: 'Narrowband IoT support',
        type: 'boolean',
        category: 'iot',
        defaultValue: false,
        order: 21,
        required: false
    },
    // Performance Metrics
    {
        id: 'maxDownloadSpeed',
        displayName: 'Max Download Speed',
        description: 'Maximum download speed',
        type: 'number',
        unit: 'Mbps',
        category: 'performance',
        defaultValue: 0,
        order: 30,
        required: true
    },
    {
        id: 'maxUploadSpeed',
        displayName: 'Max Upload Speed',
        description: 'Maximum upload speed',
        type: 'number',
        unit: 'Mbps',
        category: 'performance',
        defaultValue: 0,
        order: 31,
        required: true
    },
    {
        id: 'latency',
        displayName: 'Typical Latency',
        description: 'Typical network latency',
        type: 'number',
        unit: 'ms',
        category: 'performance',
        defaultValue: 0,
        order: 32,
        required: false
    },
    // Carrier Aggregation
    {
        id: 'carrierAggregation',
        displayName: 'Carrier Aggregation',
        description: 'Number of carrier aggregation supported',
        type: 'enum',
        options: ['None', '2CA', '3CA', '4CA', '5CA'],
        category: 'features',
        defaultValue: 'None',
        order: 40,
        required: false
    },
    // Antenna Configuration
    {
        id: 'antennaConfig',
        displayName: 'Antenna Configuration',
        description: 'MIMO antenna configuration',
        type: 'enum',
        options: ['1x1', '2x2', '4x4', '8x8'],
        category: 'features',
        defaultValue: '2x2',
        order: 41,
        required: false
    },
    // ---------------------------------------------------------------------------
    // Architecture & test-engine integration
    // ---------------------------------------------------------------------------
    {
        id: 'dataPlane',
        displayName: 'Data Plane',
        description: 'How the module exposes cellular data to the host. QMI modems use a Linux netns via qmicli; AT-only modems use the on-chip TCP/IP stack via AT commands; PPP uses the legacy dial-up interface.',
        type: 'enum',
        options: ['qmi', 'at-only', 'ppp', 'mbim'],
        category: 'features',
        defaultValue: 'qmi',
        order: 50,
        required: false
    },
    {
        id: 'voiceTransport',
        displayName: 'Voice Audio Transport',
        description: 'How modem audio reaches the host for voice tests. `uac` = USB Audio Class (ALSA card). `qpcmv` = raw PCM over a ttyUSB via AT+QPCMV. `none` = no voice support (e.g. IoT/LPWA).',
        type: 'enum',
        options: ['uac', 'qpcmv', 'none'],
        category: 'features',
        defaultValue: 'none',
        order: 52,
        required: false
    },
    {
        id: 'uacSampleRates',
        displayName: 'UAC Sample Rates',
        description: 'USB Audio Class sample rates exposed by the modem. 8000 = narrowband only; [8000,16000] = wideband also available via a second altset.',
        type: 'array',
        unit: 'Hz',
        category: 'features',
        defaultValue: [],
        order: 53,
        required: false
    },
    {
        id: 'supportsQPCMV',
        displayName: 'AT+QPCMV Raw PCM',
        description: 'Modem firmware exposes raw PCM over a ttyUSB via AT+QPCMV. Used as the fallback voice transport when UAC is unavailable.',
        type: 'boolean',
        category: 'features',
        defaultValue: false,
        order: 54,
        required: false
    },
    {
        id: 'qpcmvPorts',
        displayName: 'QPCMV Port Count',
        description: 'Number of ttyUSB ports on which AT+QPCMV can be enabled (from AT+QPCMV=? response). 0 if QPCMV is unsupported.',
        type: 'number',
        category: 'features',
        defaultValue: 0,
        order: 55,
        required: false
    },
    {
        id: 'lteCategory',
        displayName: 'LTE Category',
        description: 'LTE UE category (peak DL class). Cat 1 ≈ 10 Mbps, Cat 4 ≈ 150 Mbps, Cat 12 ≈ 600 Mbps, Cat 19 ≈ 1.6 Gbps.',
        type: 'number',
        unit: 'Cat',
        category: 'performance',
        defaultValue: 0,
        order: 33,
        required: false
    },
    {
        id: 'threeGppRelease',
        displayName: '3GPP Release',
        description: '3GPP specification release implemented by the modem firmware',
        type: 'string',
        category: 'features',
        defaultValue: '',
        order: 51,
        required: false
    },
    // ---------------------------------------------------------------------------
    // Supported frequency bands (arrays of band identifiers)
    // ---------------------------------------------------------------------------
    {
        id: 'lteFddBands',
        displayName: 'LTE-FDD Bands',
        description: 'Supported LTE-FDD band numbers (e.g., B1, B3, B20)',
        type: 'array',
        category: 'network',
        defaultValue: [],
        order: 60,
        required: false
    },
    {
        id: 'lteTddBands',
        displayName: 'LTE-TDD Bands',
        description: 'Supported LTE-TDD band numbers (e.g., B38, B40, B41)',
        type: 'array',
        category: 'network',
        defaultValue: [],
        order: 61,
        required: false
    },
    {
        id: 'laaBands',
        displayName: 'LAA Bands',
        description: 'Licensed-Assisted-Access bands used in unlicensed 5 GHz spectrum',
        type: 'array',
        category: 'network',
        defaultValue: [],
        order: 62,
        required: false
    },
    {
        id: 'nrSaBands',
        displayName: '5G NR SA Bands',
        description: '5G New Radio Standalone bands (e.g., n1, n77, n78)',
        type: 'array',
        category: 'network',
        defaultValue: [],
        order: 63,
        required: false
    },
    {
        id: 'nrNsaBands',
        displayName: '5G NR NSA Bands',
        description: '5G New Radio Non-Standalone bands',
        type: 'array',
        category: 'network',
        defaultValue: [],
        order: 64,
        required: false
    },
    {
        id: 'wcdmaBands',
        displayName: 'WCDMA / HSPA+ Bands',
        description: 'UMTS / WCDMA / HSPA+ bands (e.g., B1, B2, B5, B8)',
        type: 'array',
        category: 'network',
        defaultValue: [],
        order: 65,
        required: false
    },
    {
        id: 'gsmBands',
        displayName: 'GSM / EGPRS Bands',
        description: 'GSM / GPRS / EDGE band labels (e.g., GSM850, DCS1800) or MHz (e.g., 900, 1800)',
        type: 'array',
        category: 'network',
        defaultValue: [],
        order: 66,
        required: false
    },
    // ---------------------------------------------------------------------------
    // Certifications
    // ---------------------------------------------------------------------------
    {
        id: 'carrierCertifications',
        displayName: 'Carrier Certifications',
        description: 'Operators that have certified the module for their network (e.g., Vodafone, Verizon, AT&T, DT)',
        type: 'array',
        category: 'other',
        defaultValue: [],
        order: 70,
        required: false
    },
    {
        id: 'regulatoryApprovals',
        displayName: 'Regulatory Approvals',
        description: 'Regulatory / regional certifications (e.g., GCF, CE, FCC, PTCRB, IC, RCM)',
        type: 'array',
        category: 'other',
        defaultValue: [],
        order: 71,
        required: false
    },
    // ---------------------------------------------------------------------------
    // Modulation / protocol / voice details
    // ---------------------------------------------------------------------------
    {
        id: 'supports256QAM',
        displayName: '256QAM DL',
        description: 'Supports 256-QAM modulation on the downlink',
        type: 'boolean',
        category: 'features',
        defaultValue: false,
        order: 80,
        required: false
    },
    {
        id: 'supportsDSSA',
        displayName: 'Dual SIM Single Active',
        description: 'Dual-SIM with single active radio (DSSA)',
        type: 'boolean',
        category: 'features',
        defaultValue: false,
        order: 81,
        required: false
    },
    {
        id: 'outputPowerClass',
        displayName: 'Output Power Class',
        description: 'LTE transmit power class (e.g., "Class 3" ≈ 23 dBm, "Class 5" ≈ 21 dBm)',
        type: 'string',
        category: 'performance',
        defaultValue: '',
        order: 34,
        required: false
    },
    {
        id: 'voiceCodecs',
        displayName: 'Voice Codecs',
        description: 'Supported speech codecs (HR, FR, EFR, AMR, AMR-WB, EVS)',
        type: 'array',
        category: 'features',
        defaultValue: [],
        order: 82,
        required: false
    },
    {
        id: 'embeddedProtocols',
        displayName: 'Embedded Protocols',
        description: 'Application-layer protocols available via the on-module stack (relevant for AT-only data plane modems)',
        type: 'array',
        category: 'features',
        defaultValue: [],
        order: 83,
        required: false
    }
];
/**
 * Get capability definition by ID
 */
export function getCapabilityDefinition(id) {
    return STANDARD_CAPABILITIES.find(cap => cap.id === id);
}
/**
 * Get capabilities by category
 */
export function getCapabilitiesByCategory(category) {
    return STANDARD_CAPABILITIES.filter(cap => cap.category === category);
}
/**
 * Get all capability IDs
 */
export function getAllCapabilityIds() {
    return STANDARD_CAPABILITIES.map(cap => cap.id);
}
/**
 * Validate if a capability ID is standard
 */
export function isStandardCapability(id) {
    return STANDARD_CAPABILITIES.some(cap => cap.id === id);
}
